import type { FC } from 'react'

import { TestErrorPage } from '@skytech/monitoring'
import { LoaderCircle } from '@skytech/ui'
import { Routes, Route, Navigate } from 'react-router-dom'

import { useProfile } from '@/hooks/useProfile'
import { APP_ROUTES } from '@/util/appRoutes'

import { ActivityRoutes } from './activities/ActivityRoutes'
import Buyout from './buyout'
import { CartRoutes } from './cart/Routes'
import Dashboard from './dashboard/Dashboard'
import { EcomPolicyShop } from './ecom-policy/EcomPolicyShop'
import { GreenpoolOrderDetails } from './ecom-policy/orders/GreenpoolOrderDetails'
import { OrderDetails } from './ecom-policy/orders/OrderDetails'
import { Orders as EcomPolicyOrders } from './ecom-policy/orders/Orders'
import { EcomPolicy } from './ecom-policy/policy/Policy'
import { Guidelines } from './guidelines/Guidelines'
import HardwareOrders from './hardware-orders/HardwareOrders'
import InjuryReport from './my-assets'
import Onboarding from './onboarding/Onboarding'
import Resale from './resale/Resale'
import Shop from './shop/Shop'
import { SubscriptionServices } from './subscription-services'

export const Components: FC = () => {
  const { profile, isProfileLoading } = useProfile()

  if (!profile || isProfileLoading) {
    return <LoaderCircle />
  }

  return (
    <div
      id="base-container"
      className="h-full w-full overflow-y-auto px-2 py-2 pb-10 sm:px-4 sm:py-4 md:px-10"
    >
      <Routes>
        <Route path="*" element={<Dashboard />} />
        {/* For testing monitoring error reports */}
        {process.env.VERCEL_ENV !== 'production' && (
          <Route path="/monitoring" element={<TestErrorPage />} />
        )}

        {Boolean(profile && profile.activitiesCount > 0) && (
          <Route path="/" element={<Navigate to={APP_ROUTES.activities} replace />} />
        )}

        {Boolean(profile.allowSubscriptions) && (
          <Route path="/dashboard">
            <Route path="" element={<Dashboard />} />
            <Route path=":offset" element={<Dashboard />} />
            <Route path=":offset/:subSection" element={<Dashboard />} />
          </Route>
        )}

        <Route path="/shop">
          <Route path="" element={<Shop />} />
          <Route path=":subSection" element={<Shop />} />
        </Route>

        {Boolean(profile.allowSubscriptions) && (
          <Route path={APP_ROUTES.subscriptionServices} element={<SubscriptionServices />} />
        )}

        {Boolean(profile && profile.allowSubscriptions === 0) && (
          <Route path="/" element={<Navigate to={APP_ROUTES.ecomPolicyList} replace />} />
        )}

        <Route path={APP_ROUTES.guidelines} element={<Guidelines />} />
        <Route path={APP_ROUTES.onboarding} element={<Onboarding />} />
        <Route path={APP_ROUTES.buyout} element={<Buyout />} />
        <Route path={APP_ROUTES.myAssets} element={<InjuryReport />} />
        <Route path={APP_ROUTES.hardware} element={<HardwareOrders />} />
        <Route path={`${APP_ROUTES.resale}/*`} element={<Resale />} />
        <Route path={APP_ROUTES.ecomPolicyOrder} element={<OrderDetails />} />
        <Route path={APP_ROUTES.ecomPolicyGreenpoolOrder} element={<GreenpoolOrderDetails />} />
        <Route path={APP_ROUTES.ecomPolicyOrderList} element={<EcomPolicyOrders />} />

        <Route path={`${APP_ROUTES.ecomPolicyList}`}>
          <Route path="" element={<EcomPolicyShop />} />
          <Route path={APP_ROUTES.ecomPolicy} element={<EcomPolicy />} />
        </Route>

        <Route path={`${APP_ROUTES.cart}/*`} element={<CartRoutes />} />
        <Route path={`${APP_ROUTES.activities}/*`} element={<ActivityRoutes />} />
      </Routes>
    </div>
  )
}
